import { Link } from "react-router-dom";
import { 
  FileCheck, 
  ShoppingCart, 
  Heart, 
  ArrowRight,
  FileText,
  BarChart3,
  TrendingUp,
  BookOpen
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const gateways = [
  {
    id: "compliance",
    title: "Environmental Compliance & Approvals",
    description: "End-to-end support for all environmental licenses, permits, and ongoing compliance — from factory setup to operations.",
    icon: FileCheck,
    color: "bg-emerald-600",
    hoverColor: "group-hover:bg-emerald-600",
    lightColor: "bg-emerald-50",
    href: "/environmental-compliance",
    features: [
      "CTE & CTO Approvals",
      "SPCB & CPCB Clearances",
      "Waste Authorizations",
      "Ongoing Compliance AMC"
    ]
  },
  {
    id: "advisory",
    title: "CBAM & Carbon Compliance Advisory",
    description: "Expert guidance for carbon footprint assessment, emissions reporting, CBAM compliance support, and sustainability advisory.",
    icon: FileCheck,
    color: "bg-earth-700",
    hoverColor: "group-hover:bg-earth-700",
    lightColor: "bg-earth-50",
    href: "/advisory",
    features: [
      "Carbon footprint assessment",
      "Emissions reporting",
      "CBAM compliance support",
      "Sustainability advisory"
    ]
  },
  {
    id: "marketplace",
    title: "Carbon Credit Marketplace",
    description: "Register projects, buy or sell carbon credits, and access comprehensive market education and carbon reduction methods.",
    icon: ShoppingCart,
    color: "bg-forest-950",
    hoverColor: "group-hover:bg-forest-950",
    lightColor: "bg-forest-50",
    href: "/marketplace",
    features: [
      "Register Carbon Project",
      "Buy Carbon Credits",
      "Sell Carbon Credits",
      "Carbon Market Education"
    ]
  },
  {
    id: "bharat-initiative",
    title: "Bharat Climate Contribution Initiative",
    subtitle: "One Credit for the Planet",
    description: "Enable individuals and institutions to participate in nation-scale climate action, supporting India's transition towards a sustainable future.",
    icon: Heart,
    color: "bg-orange-600",
    hoverColor: "group-hover:bg-orange-600",
    lightColor: "bg-orange-50",
    href: "/initiatives/bharat-climate",
    cta: "Contribute to Bharat Climate Initiative"
  },
];

export default function CoreGateways() {
  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="core-gateways">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="font-montserrat text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Your Gateway to Climate Action
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Pathways to participate in India's carbon economy and drive meaningful climate impact.
          </p>
        </div>

        {/* Gateway Cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {gateways.map((gateway) => (
            <Link
              key={gateway.id}
              to={gateway.href}
              className="group"
              data-testid={`gateway-${gateway.id}`}
            >
              <Card className="h-full gateway-card border-slate-100 hover:border-slate-200 overflow-hidden">
                <CardContent className="p-6 lg:p-8">
                  {/* Icon and Title */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`p-3 rounded-xl ${gateway.lightColor} ${gateway.hoverColor} group-hover:text-white transition-all duration-300`}>
                      <gateway.icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-montserrat text-xl font-semibold text-slate-900 group-hover:text-forest-950 transition-colors">
                        {gateway.title}
                      </h3>
                      {gateway.subtitle && (
                        <p className="text-sm font-medium text-orange-600 mt-1">
                          {gateway.subtitle}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    {gateway.description}
                  </p>

                  {/* Features List */}
                  {gateway.features && (
                    <ul className="space-y-2 mb-4">
                      {gateway.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                          <div className={`w-1.5 h-1.5 rounded-full ${gateway.color}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-sm font-medium text-forest-950 group-hover:gap-3 transition-all">
                    {gateway.cta || "Explore"}
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
